import { encodePacked, getCreate2Address, keccak256 } from 'viem'
import { dolomiteConfigs, dolomiteIsolationMarket } from '@1delta/data-sdk'
import { Call } from '../../../utils/multicall'
import { DOLOMITE_SUBGRAPH_URLS } from '../../dolomite-subgraph'

// ---------------------------------------------------------------------------
// Subgraph fetcher with memoization (build + parse share one result)
// Endpoints live in ../dolomite-subgraph (shared with the public-data route).
// ---------------------------------------------------------------------------

const SUBGRAPH_CACHE_TTL_MS = 30_000
const SUBGRAPH_TIMEOUT_MS = 10_000

/**
 * One Dolomite sub-account as the READ path sees it: `(owner, number)`.
 *
 * For an ordinary account `owner` is the user. For an isolation-mode position
 * (dGM, dsavETH, dGMX, …) `owner` is the user's per-factory VAULT — the vault,
 * not the user, owns the DolomiteMargin account — and `isolationMarketId` is
 * the factory's marketId, which is exactly the `isolationModeMarketId` the
 * Dolomite routers take to reach that vault again. `id` is the position id
 * the API exposes: the bare account number for the user's own accounts, and
 * `iso:<marketId>:<accountNumber>` for vault-owned ones, so an action can be
 * addressed without ever carrying the vault address.
 */
export interface DolomiteSubAccount {
  id: string
  owner: string
  number: string
  isolationMarketId?: string
}

export const DOLOMITE_ISO_ID_PREFIX = 'iso:'

/** Parse a position id back into `(isolationMarketId?, accountNumber)`. */
export function parseDolomiteSubAccountId(id: string): {
  isolationMarketId?: string
  number: string
} {
  if (id.startsWith(DOLOMITE_ISO_ID_PREFIX)) {
    const [, marketId, number] = id.split(':')
    return { isolationMarketId: marketId, number }
  }
  return { number: id }
}

export function toDolomiteSubAccountId(
  number: string,
  isolationMarketId?: string,
): string {
  return isolationMarketId
    ? `${DOLOMITE_ISO_ID_PREFIX}${isolationMarketId}:${number}`
    : number
}

/**
 * The vault that owns `user`'s position in isolation market `marketId`
 * (`IsolationModeVaultFactory.calculateVaultByAccount`), derived offline from
 * the factory's `vaultInitCodeHash`: `CREATE2(factory, keccak256(user), hash)`.
 * Verified bit-exact against `getVaultByAccount` on Arbitrum (2026-09-18).
 * The address is deterministic whether or not the vault exists yet — reading
 * a not-yet-created vault answers an empty account. `undefined` when the
 * market is not an isolation market or its hash is unknown (the dead djUSDC
 * V1 factory predates the getter).
 */
export function dolomiteVaultAddress(
  chainId: string,
  marketId: string | number | bigint,
  user: string,
): string | undefined {
  const m = dolomiteIsolationMarket(chainId, marketId)
  if (!m?.vaultInitCodeHash) return undefined
  return getCreate2Address({
    from: m.factory as `0x${string}`,
    salt: keccak256(encodePacked(['address'], [user as `0x${string}`])),
    bytecodeHash: m.vaultInitCodeHash as `0x${string}`,
  }).toLowerCase()
}

const subgraphCache = new Map<
  string,
  {
    promise: Promise<DolomiteSubAccount[]>
    timer: ReturnType<typeof setTimeout>
  }
>()

/**
 * Every funded / borrowing sub-account an owner controls — their own accounts
 * AND the accounts of their isolation-mode vaults — from the Dolomite
 * subgraph. Memoized 30 s so concurrent build + parse calls share one fetch.
 * Always includes the user's default account `'0'`. Falls back to just that
 * on any error.
 */
export function fetchDolomiteSubAccounts(
  chainId: string,
  owner: string,
): Promise<DolomiteSubAccount[]> {
  const key = `${chainId}:${owner.toLowerCase()}`
  const cached = subgraphCache.get(key)
  if (cached) return cached.promise

  const promise = fetchSubAccountsFromSubgraph(chainId, owner)
    .then((accounts) => withDefault(owner, accounts))
    .catch(() => withDefault(owner, []))

  const timer = setTimeout(
    () => subgraphCache.delete(key),
    SUBGRAPH_CACHE_TTL_MS,
  )
  subgraphCache.set(key, { promise, timer })
  return promise
}

/**
 * The account NUMBERS the owner holds directly (not through a vault) — the
 * shape `/next-account` and explicit-`accountNumbers` callers work with.
 * Always includes `'0'`.
 */
export async function fetchDolomiteAccountNumbers(
  chainId: string,
  owner: string,
): Promise<string[]> {
  const accounts = await fetchDolomiteSubAccounts(chainId, owner)
  return accounts.filter((a) => !a.isolationMarketId).map((a) => a.number)
}

function withDefault(
  owner: string,
  accounts: DolomiteSubAccount[],
): DolomiteSubAccount[] {
  const seen = new Set<string>()
  const out: DolomiteSubAccount[] = []
  const push = (a: DolomiteSubAccount) => {
    if (seen.has(a.id)) return
    seen.add(a.id)
    out.push(a)
  }
  push({ id: '0', owner, number: '0' })
  accounts.forEach(push)
  return out
}

async function fetchSubAccountsFromSubgraph(
  chainId: string,
  owner: string,
): Promise<DolomiteSubAccount[]> {
  const url = DOLOMITE_SUBGRAPH_URLS[chainId]
  if (!url) return []

  // `effectiveUser` resolves the ownership chain: a vault-owned account's
  // `user` is the vault address and its `effectiveUser` is the EOA. A query on
  // `user` (what this did before) silently drops every isolation position.
  // Only funded / borrowing accounts — skip empty closed sub-accounts.
  //
  // The filter MUST nest the field under an explicit `and`: graph-node rejects
  // `{ user: …, or: […] }` with "Filter must by an object", and since that
  // error was swallowed into the `['0']` fallback, the previous shape had been
  // serving ONLY the default account for every Dolomite user — no
  // sub-account, isolation or not, was ever read. Never let this query's
  // failure be silent again: the catch below logs.
  const query = `{
  marginAccounts(
    first: 1000
    where: { and: [
      { effectiveUser: "${owner.toLowerCase()}" },
      { or: [{ hasSupplyValue: true }, { hasBorrowValue: true }] }
    ] }
  ) {
    accountNumber
    user { id isolationModeVault { marketId } }
  }
}`

  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), SUBGRAPH_TIMEOUT_MS)
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query }),
      signal: controller.signal,
    })
    if (!response.ok) {
      console.warn(
        `[dolomite] sub-account query for ${owner} on chain ${chainId} answered HTTP ${response.status}; serving the default account only`,
      )
      return []
    }
    const data = (await response.json()) as {
      data?: {
        marginAccounts?: {
          accountNumber: string
          user: { id: string; isolationModeVault: { marketId: string } | null }
        }[]
      }
      errors?: { message: string }[]
    }
    if (data?.errors?.length) {
      console.warn(
        `[dolomite] sub-account query for ${owner} on chain ${chainId} rejected: ${data.errors.map((e) => e.message).join('; ')}; serving the default account only`,
      )
      return []
    }
    const accounts = data?.data?.marginAccounts
    if (!accounts || accounts.length === 0) return []
    const lowerOwner = owner.toLowerCase()
    return accounts.map((a) => {
      const number = String(a.accountNumber)
      const accountOwner = a.user.id.toLowerCase()
      const isolationMarketId =
        accountOwner !== lowerOwner
          ? a.user.isolationModeVault?.marketId
          : undefined
      return {
        id: toDolomiteSubAccountId(number, isolationMarketId),
        owner: accountOwner,
        number,
        isolationMarketId,
      }
    })
  } finally {
    clearTimeout(timeout)
  }
}

// ---------------------------------------------------------------------------
// Resolved sub-accounts store — written by build, read by parse (sync)
// ---------------------------------------------------------------------------

const RESOLVED_STORE_TTL_MS = 60_000

const resolvedStore = new Map<
  string,
  { accounts: DolomiteSubAccount[]; timer: ReturnType<typeof setTimeout> }
>()

/**
 * An explicit position id → sub-account, with no subgraph: a plain number is
 * the user's own account; `iso:<marketId>:<n>` is account `n` of the user's
 * vault for that market, whose address is derived offline. An `iso:` id on a
 * market with no derivable vault is dropped (logged), never read as the
 * user's own account — that would silently show an empty position.
 */
function toSubAccount(
  chainId: string,
  account: string,
  id: string,
): DolomiteSubAccount[] {
  const parsed = parseDolomiteSubAccountId(id)
  if (parsed.isolationMarketId === undefined) {
    return [{ id, owner: account, number: parsed.number }]
  }
  const vault = dolomiteVaultAddress(chainId, parsed.isolationMarketId, account)
  if (!vault) {
    console.warn(
      `[dolomite] cannot derive the vault for position ${id} on chain ${chainId} (no isolation table / init-code hash); skipped`,
    )
    return []
  }
  return [
    {
      id: toDolomiteSubAccountId(parsed.number, parsed.isolationMarketId),
      owner: vault,
      number: parsed.number,
      isolationMarketId: parsed.isolationMarketId,
    },
  ]
}

/** Synchronous read of the sub-accounts a prior build resolved. */
export function getResolvedDolomiteSubAccounts(
  chainId: string,
  account: string,
): DolomiteSubAccount[] | undefined {
  return resolvedStore.get(`${chainId}:${account.toLowerCase()}`)?.accounts
}

/** Legacy shape: the resolved position ids (numbers, or `iso:` ids). */
export function getResolvedDolomiteAccountNumbers(
  chainId: string,
  account: string,
): string[] | undefined {
  return getResolvedDolomiteSubAccounts(chainId, account)?.map((a) => a.id)
}

function storeResolved(
  chainId: string,
  account: string,
  accounts: DolomiteSubAccount[],
): void {
  const key = `${chainId}:${account.toLowerCase()}`
  const existing = resolvedStore.get(key)
  if (existing) clearTimeout(existing.timer)
  const timer = setTimeout(
    () => resolvedStore.delete(key),
    RESOLVED_STORE_TTL_MS,
  )
  resolvedStore.set(key, { accounts, timer })
}

// ---------------------------------------------------------------------------
// Build multicall calls
// ---------------------------------------------------------------------------

/**
 * Build user-data calls for Dolomite. One `getAccountBalances` per sub-account
 * `(owner, number)` — the owner being the user's VAULT for isolation-mode
 * positions. Sub-accounts come from `accountNumbers` when provided (plain
 * account numbers, or `iso:<marketId>:<n>` ids resolved offline through the
 * factory's CREATE2 hash — e.g. the one-shot routes, `?subAccounts=`), else
 * the subgraph, else `['0']`.
 *
 * NOTE (e-mode / Model B): to make health exact for accounts under a margin-ratio
 * override, add a second `getAccountRiskOverrideByAccount` call per sub-account
 * here and recompute collateral factors per account in the parser. v1 ships the
 * base path (one call/account); the override is a conservative no-op for now.
 */
export const buildDolomiteUserCall = async (
  chainId: string,
  _lender: string,
  account: string,
  accountNumbers?: string[],
): Promise<Call[]> => {
  const margin = dolomiteConfigs()?.[chainId]?.dolomiteMargin
  if (!margin) return []

  const accounts: DolomiteSubAccount[] = accountNumbers
    ? withDefault(
        account,
        accountNumbers.flatMap((id) => toSubAccount(chainId, account, id)),
      )
    : await fetchDolomiteSubAccounts(chainId, account)
  storeResolved(chainId, account, accounts)

  return accounts.map(({ owner, number }) => ({
    address: margin,
    name: 'getAccountBalances',
    params: [{ owner, number }],
  }))
}
